import React from 'react';
import styled, { useTheme } from 'styled-components';

export interface KpiCardProps {
  label: string;
  value: string | number;
  unit?: string;
  sub?: string;
  tone?: 'default' | 'success' | 'warn' | 'error';
  children?: React.ReactNode;
  className?: string;
}

const Surface = styled.div`
  background: ${(p) => p.theme.colors.neutral.panel};
  border: 1px solid ${(p) => p.theme.colors.neutral.line};
  border-radius: ${(p) => p.theme.radius.card};
  box-shadow: ${(p) => p.theme.elevation.flat.shadow};
  padding: ${(p) => p.theme.spaceAlias.md};
  display: flex;
  flex-direction: column;
  gap: ${(p) => p.theme.spaceAlias.sm};
  min-height: 118px;
`;

const Label = styled.div`
  font-family: ${(p) => p.theme.fontFamily.ui};
  font-size: ${(p) => p.theme.fontSize.xs};
  font-weight: ${(p) => p.theme.fontWeight.bold};
  letter-spacing: 0.8px;
  text-transform: uppercase;
  color: ${(p) => p.theme.colors.neutral.inkMute};
`;

const ValueRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: ${(p) => p.theme.spaceAlias.xs};
`;

const HeroValue = styled.span<{ $color: string }>`
  font-family: ${(p) => p.theme.fontFamily.mono};
  font-size: ${(p) => p.theme.fontSize.display};
  font-weight: ${(p) => p.theme.fontWeight.bold};
  color: ${(p) => p.$color};
  letter-spacing: -1px;
  line-height: 1;
`;

const Unit = styled.span`
  font-family: ${(p) => p.theme.fontFamily.mono};
  font-size: ${(p) => p.theme.fontSize.md};
  font-weight: ${(p) => p.theme.fontWeight.medium};
  color: ${(p) => p.theme.colors.neutral.inkSoft};
`;

const Sub = styled.div`
  font-family: ${(p) => p.theme.fontFamily.ui};
  font-size: ${(p) => p.theme.fontSize.sm};
  color: ${(p) => p.theme.colors.neutral.inkSoft};
  line-height: 1.4;
`;

const VizSlot = styled.div`
  flex: 1;
  display: flex;
  align-items: flex-end;
  min-height: ${(p) => p.theme.spaceAlias.md};
`;

export function KpiCard({
  label,
  value,
  unit,
  sub,
  tone = 'default',
  children,
  className,
}: KpiCardProps) {
  const theme = useTheme();
  const toneColor =
    tone === 'success'
      ? theme.colors.semantic.success.color
      : tone === 'warn'
        ? theme.colors.semantic.warn.color
        : tone === 'error'
          ? theme.colors.semantic.error.color
          : theme.colors.neutral.ink;

  return (
    <Surface className={className}>
      <Label>{label}</Label>
      <ValueRow>
        <HeroValue $color={toneColor} data-kpi-value>
          {value}
        </HeroValue>
        {unit && <Unit>{unit}</Unit>}
      </ValueRow>
      {sub && <Sub>{sub}</Sub>}
      {children && <VizSlot>{children}</VizSlot>}
    </Surface>
  );
}
